import React from "react";
import { Box, Grid, Theme, Typography, Dialog } from "@mui/material";


/* ─────────────────────────────
   1.  DATA
   ───────────────────────────── */
interface ImpactCardData {
  number: string;
  label: string;
  context: string;
  color: string;
}

const impactCards: ImpactCardData[] = [
  {
    number: "-85%",
    label: "DATA-ENTRY TIME",
    context: "400+ analysts",
    color: "#0021F5",
  },
  {
    number: "0",
    label: "OVERWRITE INCIDENTS",
    context: "First-quarter post launch",
    color: "#0021F5",
  },
];

/* ─────────────────────────────
   2.  BASE + RESPONSIVE STYLES
   ───────────────────────────── */
const styles: Record<string, React.CSSProperties> = {
  impactSection: { marginBottom: "40px", marginLeft: "60px", marginTop: "120px" },

  impactHeader: {
    display: "flex",
    alignItems: "baseline",
    gap: "20px",
    marginBottom: "40px",
  },
  impactTitle: {
    fontSize: "clamp(60px, 8vw, 100px)",
    fontWeight: 900,
    fontStyle: "italic",
    textTransform: "uppercase",
    color: "#0021F5",
    letterSpacing: "-4px",
    lineHeight: "0.9",
    margin: 0,
  },
  impactSubtitle: {
    fontSize: "18px",
    color: "#666",
    fontWeight: 500,
    margin: 0,
  },

  impactGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 320px))",    gap: "30px",
    marginBottom: "80px",
  },
  impactCard: {
    color: "#fff",
    padding: "40px",
    position: "relative",
    overflow: "hidden",
    cursor: "pointer",
    transition: "all 0.3s ease",
    animation: "fadeInUp 0.8s ease-out forwards",
  },
  impactCardBefore: {
    position: "absolute",
    top: "-50%",
    right: "-50%",
    width: "200%",
    height: "200%",
    background:
      "radial-gradient(circle, rgba(173,255,0,0.3) 0%, transparent 70%)",
    animation: "pulse 3s ease-in-out infinite",
    pointerEvents: "none" as const,
  },
  impactNumber: {
    fontSize: "clamp(48px, 6vw, 72px)",
    fontWeight: 900,
    marginBottom: "10px",
    color: "#ADFF00",
    textShadow: "2px 2px 0 rgba(0,0,0,0.1)",
    position: "relative",
    zIndex: 1,
  },
  impactLabel: {
    fontSize: "18px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "1px",
    position: "relative",
    zIndex: 1,
  },
  impactContext: {
    fontSize: "14px",
    marginTop: "5px",
    position: "relative",
    zIndex: 1,
  },
};

/* tweaks at specific break-points */
const mediaStyles = {
  mobile: {
    impactHeader: {
      flexDirection: "column",
      gap: "10px",
      alignItems: "flex-start",
    },
    impactTitle: {
      fontSize: "clamp(48px, 12vw, 72px)",
    },
    impactGrid: {
      gridTemplateColumns: "1fr",
      gap: "20px",
    },
    impactCard: { padding: "30px" },
    impactNumber: {
      fontSize: "clamp(40px, 10vw, 56px)",
    },
  },
  tablet: {
    impactGrid: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
} as const;

/* ─────────────────────────────
   3.  COMPONENT
   ───────────────────────────── */
const ImpactSection: React.FC = () => {
  const [windowWidth, setWindowWidth] = React.useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  React.useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* merge base + responsive overrides */
  const getResponsiveStyles = (
    base: React.CSSProperties,
    key: string
  ): React.CSSProperties => {
    if (windowWidth <= 768 && (mediaStyles.mobile as any)[key]) {
      return { ...base, ...(mediaStyles.mobile as any)[key] };
    }
    if (
      windowWidth > 768 &&
      windowWidth <= 1024 &&
      (mediaStyles.tablet as any)[key]
    ) {
      return { ...base, ...(mediaStyles.tablet as any)[key] };
    }
    return base;
  };

  return (
    <>
      {/* keyframes + hover state */}
      <style>
        {`
          @keyframes fadeInUp {
            from { opacity:0; transform: translateY(30px); }
            to   { opacity:1; transform: translateY(0);     }
          }
          @keyframes pulse {
            0%,100% { transform:scale(1);   opacity:.5; }
            50%     { transform:scale(1.1); opacity:.3; }
          }
          
          .impact-card:nth-child(1){animation-delay:1s}
          .impact-card:nth-child(2){animation-delay:1.1s}
          .impact-card:nth-child(3){animation-delay:1.2s}
          .impact-card:nth-child(4){animation-delay:1.3s}
        `}
      </style>

      <div style={styles.impactSection}>
        {/* header */}
        <Typography
              variant="h3"
              sx={{ fontSize: { md: "28px", xs: "22px" }, fontWeight: 600, mb: 4 }}
            >
Impact</Typography>

        {/* grid */}
        <div style={getResponsiveStyles(styles.impactGrid, "impactGrid")}>
          {impactCards.map((c, i) => (
            <div
              key={i}
              className="impact-card"
              style={{
                ...getResponsiveStyles(styles.impactCard, "impactCard"),
                backgroundColor: c.color,
                animationDelay: `${1 + i * 0.1}s`,
              }}
            >
              <div style={styles.impactCardBefore} />
              <div
                style={getResponsiveStyles(styles.impactNumber, "impactNumber")}
              >
                {c.number}
              </div>
              <div style={styles.impactLabel}>{c.label}</div>
              <div style={styles.impactContext}>{c.context}</div>
            </div>
          ))}
        </div>

      </div>
    </>
  );
};

export default ImpactSection;
